import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FurnitureList from '../../components/furniture/FurnitureList';
import FurnitureFilter from '../../components/furniture/FurnitureFilter';
import { FurnitureFilters } from '../../services/furniture';
import { useTranslation } from 'react-i18next';

const FurnitureListingPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FurnitureFilters>({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Available filter options (canonical enums — LAUNCH.md §2)
  const categories = ['sofa', 'table', 'chair', 'desk', 'bed', 'dresser', 'storage', 'lighting', 'other'];
  const conditions = ['like-new', 'good', 'fair', 'needs-repair'];

  useEffect(() => {
    // Extract search query and category from URL params
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get('search');
    const categoryQuery = queryParams.get('category');
    const neighborhoodQuery = queryParams.get('neighborhood');

    // Update filters based on URL parameters
    const newFilters: Partial<FurnitureFilters> = {};

    if (searchQuery) {
      setSearchTerm(searchQuery);
      newFilters.search = searchQuery;
    }

    if (categoryQuery) {
      newFilters.category = categoryQuery;
    }

    if (neighborhoodQuery) {
      newFilters.neighborhood = neighborhoodQuery;
    }

    // Only update filters if we have any changes to make
    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({
        ...prev,
        ...newFilters
      }));
    }
  }, [location.search]);

  // Handle page change events from FurnitureList component
  useEffect(() => {
    const handlePageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.page) {
        setFilters(prev => ({
          ...prev,
          page: customEvent.detail.page
        }));
      }
    };

    window.addEventListener('pageChange', handlePageChange);

    return () => {
      window.removeEventListener('pageChange', handlePageChange);
    };
  }, []);

  const handleFilterChange = (newFilters: Partial<FurnitureFilters>) => {
    const updatedFilters = { ...filters, ...newFilters, page: 1 };
    setFilters(updatedFilters);

    // 获取当前URL参数
    const params = new URLSearchParams(location.search);
    let shouldUpdateUrl = false;

    // 更新search参数
    if (newFilters.search !== undefined && newFilters.search !== filters.search) {
      setSearchTerm(newFilters.search);
      if (newFilters.search) {
        params.set('search', newFilters.search);
      } else {
        params.delete('search');
      }
      shouldUpdateUrl = true;
    }

    // 更新category参数
    if (newFilters.category !== undefined && newFilters.category !== filters.category) {
      if (newFilters.category) {
        params.set('category', newFilters.category);
      } else {
        params.delete('category');
      }
      shouldUpdateUrl = true;
    }

    if (newFilters.neighborhood !== undefined && newFilters.neighborhood !== filters.neighborhood) {
      if (newFilters.neighborhood) {
        params.set('neighborhood', newFilters.neighborhood);
      } else {
        params.delete('neighborhood');
      }
      shouldUpdateUrl = true;
    }

    // 更新URL如果有任何参数改变
    if (shouldUpdateUrl) {
      navigate({
        pathname: location.pathname,
        search: params.toString()
      }, { replace: true });
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {searchTerm ? t('furniture.listing.searchResultsFor', { term: searchTerm }) : t('furniture.listing.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {searchTerm ? t('furniture.listing.searchDescription') : t('furniture.listing.browseDescription')}
        </p>
      </div>

      <div className="mb-6">
        <FurnitureFilter
          initialSearchTerm={searchTerm}
          initialCategory={filters.category}
          categories={categories}
          conditions={conditions}
          onFilterChange={handleFilterChange}
        />
      </div>

      <FurnitureList filters={filters} />
    </div>
  );
};

export default FurnitureListingPage;
